import { useMigrateDocument, vaultKeys } from '@/hooks/useVault';
import { VaultDocument } from '@/types/vault.types';
import {
  unlockVault as unlockVaultCrypto,
  decryptDocumentComplete,
  decryptDocumentWithRSA,
} from '@/utils/crypto/vaultCrypto';
import { APP_CONFIG } from '@/constants/app';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface MigrateDocumentModalProps {
  vaultId: string;
  documents: VaultDocument[];
  isOpen: boolean;
  onClose: () => void;
  vaultData: {
    vaultKeySalt?: string | null;
    encryptedVaultKey?: {
      encryptedKey: string;
      iv: string;
      authTag: string;
    } | null;
    encryptedPrivateKey?: string | null;
    publicKey?: string | null;
  };
}

interface MigrationProgress {
  current: number;
  total: number;
  currentFilename: string;
  status: 'idle' | 'decrypting' | 'uploading' | 'done' | 'error';
  errors: string[];
  completed: number;
}

export const MigrateDocumentModal = ({
  vaultId,
  documents,
  isOpen,
  onClose,
  vaultData,
}: MigrateDocumentModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const migrateDocument = useMigrateDocument(vaultId);

  const [password, setPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<MigrationProgress>({
    current: 0,
    total: documents.length,
    currentFilename: '',
    status: 'idle',
    errors: [],
    completed: 0,
  });

  const handleMigrate = async () => {
    if (!password) {
      toast.error(
        t('vault.migrate.passwordRequired', 'Veuillez entrer le mot de passe')
      );
      return;
    }

    if (!vaultData.vaultKeySalt || !vaultData.encryptedVaultKey) {
      toast.error(
        t(
          'vault.migrate.noEncryptionData',
          'Donn\u00e9es de chiffrement manquantes'
        )
      );
      return;
    }

    setIsProcessing(true);
    setProgress({
      current: 0,
      total: documents.length,
      currentFilename: '',
      status: 'decrypting',
      errors: [],
      completed: 0,
    });

    try {
      // 1. Derive master key from old password
      const masterKey = await unlockVaultCrypto(
        password,
        vaultData.vaultKeySalt,
        vaultData.encryptedVaultKey
      );

      const errors: string[] = [];
      let completed = 0;

      // 2. Process each document
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        setProgress(prev => ({
          ...prev,
          current: i + 1,
          currentFilename: doc.originalFilename,
          status: 'decrypting',
        }));

        try {
          // 2a. Download encrypted document
          const downloadUrl = `${APP_CONFIG.api.baseUrl}/vaults/${vaultId}/documents/${doc.uuid}/download`;
          const response = await fetch(downloadUrl, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          });

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error(
                'Fichier manquant dans le stockage (supprim\u00e9 ou perdu)'
              );
            }
            throw new Error(
              `\u00c9chec du t\u00e9l\u00e9chargement (${response.status})`
            );
          }

          // 2b. Extract encryption headers
          const encryptedDocumentKey = response.headers.get('X-Encryption-Key');
          const iv = response.headers.get('X-Encryption-IV');
          const authTag = response.headers.get('X-Encryption-Auth-Tag');
          const documentKeyIv = response.headers.get('X-Document-Key-IV');
          const documentKeyAuthTag = response.headers.get(
            'X-Document-Key-Auth-Tag'
          );
          const encryptionMode = response.headers.get('X-Encryption-Mode');

          // If document is already server-side decrypted, use content directly
          if (encryptionMode === 'server_side') {
            // Already server-side, skip
            completed++;
            setProgress(prev => ({ ...prev, completed }));
            continue;
          }

          if (!encryptedDocumentKey || !iv || !authTag) {
            throw new Error('Missing encryption metadata in response headers');
          }

          const encryptedContent = await response.arrayBuffer();

          // 2c. Decrypt document client-side
          const isRSAEncrypted = !documentKeyIv || !documentKeyAuthTag;
          let decrypted: ArrayBuffer;

          if (isRSAEncrypted) {
            if (!vaultData.encryptedPrivateKey) {
              throw new Error('RSA private key not available');
            }
            decrypted = await decryptDocumentWithRSA(
              encryptedContent,
              encryptedDocumentKey,
              iv,
              authTag,
              vaultData.encryptedPrivateKey,
              masterKey
            );
          } else {
            decrypted = await decryptDocumentComplete(
              encryptedContent,
              encryptedDocumentKey,
              iv,
              authTag,
              documentKeyIv,
              documentKeyAuthTag,
              masterKey
            );
          }

          // 2d. Upload decrypted content to server for re-encryption
          setProgress(prev => ({ ...prev, status: 'uploading' }));

          const decryptedBlob = new Blob([decrypted], {
            type: doc.mimeType || 'application/octet-stream',
          });

          await migrateDocument.mutateAsync({
            documentId: doc.uuid,
            file: decryptedBlob,
          });

          completed++;
          setProgress(prev => ({ ...prev, completed }));
        } catch (cause) {
          const error = cause as HttpApiError;
          const errorMsg = `${doc.originalFilename}: ${error.message || 'Erreur inconnue'}`;
          errors.push(errorMsg);
          setProgress(prev => ({
            ...prev,
            errors: [...prev.errors, errorMsg],
          }));
        }
      }

      // 3. Done
      setProgress(prev => ({
        ...prev,
        status: 'done',
        completed,
        errors,
      }));

      // Invalidate document cache to refresh list
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });

      if (errors.length === 0) {
        toast.success(
          t(
            'vault.migrate.success',
            '{{count}} document(s) migr\u00e9(s) avec succ\u00e8s',
            {
              count: completed,
            }
          )
        );
      } else {
        toast.warning(
          t(
            'vault.migrate.partial',
            '{{completed}}/{{total}} document(s) migr\u00e9(s)',
            {
              completed,
              total: documents.length,
            }
          )
        );
      }
    } catch (cause) {
      const error = cause as HttpApiError;
      console.error('[MigrateDocumentModal] Migration failed:', error);
      setProgress(prev => ({ ...prev, status: 'error' }));
      toast.error(
        t(
          'vault.migrate.passwordError',
          'Mot de passe incorrect ou erreur de d\u00e9chiffrement'
        )
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    setPassword('');
    setProgress({
      current: 0,
      total: documents.length,
      currentFilename: '',
      status: 'idle',
      errors: [],
      completed: 0,
    });
    onClose();
  };

  if (!isOpen) return null;

  const progressPercent =
    progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          disabled={isProcessing}
          className="absolute top-4 right-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-50"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <RefreshCw className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-neutral-900">
            {t('vault.migrate.title', 'Migrer les documents')}
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            {t(
              'vault.migrate.description',
              "Entrez l'ancien mot de passe du coffre pour rendre {{count}} document(s) disponible(s)",
              { count: documents.length }
            )}
          </p>
        </div>

        {progress.status === 'idle' || progress.status === 'error' ? (
          /* Password Form */
          <div className="space-y-4">
            {/* Warning */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <p className="text-xs text-amber-700">
                  {t(
                    'vault.migrate.warning',
                    "Ces documents ont \u00e9t\u00e9 chiffr\u00e9s avec l'ancien mode de chiffrement. L'ancien mot de passe du coffre est n\u00e9cessaire pour les d\u00e9chiffrer et les re-chiffrer avec le nouveau mode."
                  )}
                </p>
              </div>
            </div>

            {/* Document list preview */}
            {documents.length > 1 && (
              <div className="max-h-32 overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                <p className="mb-1 text-xs font-medium text-neutral-500">
                  {t(
                    'vault.migrate.documentsToMigrate',
                    'Documents \u00e0 migrer :'
                  )}
                </p>
                {documents.map(doc => (
                  <div
                    key={doc.uuid}
                    className="truncate text-xs text-neutral-700"
                  >
                    {doc.originalFilename}
                  </div>
                ))}
              </div>
            )}

            {progress.status === 'error' && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-700">
                  {t(
                    'vault.migrate.passwordError',
                    'Mot de passe incorrect ou erreur de d\u00e9chiffrement'
                  )}
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="migrate-password"
                className="mb-2 block text-sm font-medium text-neutral-700"
              >
                {t('vault.migrate.password', 'Ancien mot de passe du coffre')}
                <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                id="migrate-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && password) {
                    handleMigrate();
                  }
                }}
                className="focus:border-primary-500 focus:ring-primary-100 block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:ring-2 focus:outline-none"
                placeholder="••••••••••••"
                autoFocus
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                onClick={handleMigrate}
                disabled={!password || isProcessing}
                className="bg-primary-600 hover:bg-primary-700 flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" />
                {t('vault.migrate.submit', 'Migrer')}
              </button>
            </div>
          </div>
        ) : progress.status === 'done' ? (
          /* Success */
          <div className="space-y-4">
            <div className="text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              <p className="mt-3 text-sm text-neutral-700">
                {progress.errors.length === 0
                  ? t(
                      'vault.migrate.allDone',
                      'Tous les documents ont \u00e9t\u00e9 migr\u00e9s avec succ\u00e8s !'
                    )
                  : t(
                      'vault.migrate.partialDone',
                      '{{completed}}/{{total}} document(s) migr\u00e9(s)',
                      {
                        completed: progress.completed,
                        total: progress.total,
                      }
                    )}
              </p>
            </div>

            {progress.errors.length > 0 && (
              <div className="max-h-32 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="mb-1 text-xs font-medium text-red-600">
                  {t('vault.migrate.errors', 'Erreurs :')}
                </p>
                {progress.errors.map((err, i) => (
                  <div key={i} className="truncate text-xs text-red-700">
                    {err}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleClose}
              className="bg-primary-600 hover:bg-primary-700 w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              {t('common.close', 'Fermer')}
            </button>
          </div>
        ) : (
          /* Progress */
          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-neutral-600">
                  {progress.status === 'decrypting'
                    ? t('vault.migrate.decrypting', 'D\u00e9chiffrement...')
                    : t('vault.migrate.uploading', 'Envoi au serveur...')}
                </span>
                <span className="font-medium text-neutral-900">
                  {progress.current}/{progress.total}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="bg-primary-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <p className="truncate text-xs text-neutral-500">
              {progress.currentFilename}
            </p>

            {progress.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-2">
                <p className="text-xs text-red-600">
                  {progress.errors.length} erreur(s)
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
